import { SettingOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Badge, Tabs } from 'antd';
import { useMemo, useEffect } from 'react';

import { Drawer } from 'components/feedback/drawer/Drawer';
import { Button } from 'components/general/Button';

import {
  useGetInboxNotifications,
  useMarkAllNotificationsAsRead,
} from 'hooks/inbox-notifications/useInboxNotifications';

import { imageConfig } from 'utils/imageConfig';

import { InboxNotificationProvider } from 'layout/inbox-notifications/InboxNotificationContext';
import { InboxNotificationItem } from 'layout/inbox-notifications/InboxNotificationItem';
import { InboxNotificationLoader } from 'layout/inbox-notifications/InboxNotificationLoader';
import { unreadNotificationsColor } from 'layout/inbox-notifications/_notificationUtils';

import type { InboxBaseNotification } from 'types/inbox-notifications/InboxNotifications';

export type NotificationTabType = 'unread' | 'all';

const noDataMessage = (isAllTab: boolean) => (
  <div className="flex flex-col items-center justify-center py-8 pt-20">
    <img
      src={imageConfig.no_notification}
      alt="No notifications"
      className="w-46 h-46"
    />
    <p className="font-medium">
      {`You don't have any ${isAllTab ? '' : 'new '}notifications`}
    </p>
    <p className="text-muted mt-1">
      {'New updates and alerts will appear here'}
    </p>
  </div>
);

export function InboxNotificationsDrawer({
  loading,
  open,
  onClose,
  unreadNotifications,
  activeTab,
  onTabChange,
}: {
  loading: boolean;
  open: boolean;
  onClose: () => void;
  unreadNotifications: InboxBaseNotification[];
  activeTab: NotificationTabType;
  onTabChange: (tab: NotificationTabType) => void;
}) {
  const markAsReadMutation = useMarkAllNotificationsAsRead();
  const queryClient = useQueryClient();

  // Silently refetch notifications when drawer opens
  useEffect(() => {
    if (open) {
      queryClient.invalidateQueries({ queryKey: ['notifications/v1/unread'] });
      queryClient.invalidateQueries({ queryKey: ['notifications/v1/all'] });
    }
  }, [open, queryClient]);

  // Only fetch all notifications when the "All" tab is active
  const {
    data: allNotificationsData,
    isLoading: isLoadingAllNotifications,
    isError: isErrorAllNotifications,
    refetch: refetchAllNotifications,
  } = useGetInboxNotifications({
    enabled: activeTab === 'all' && open,
  });

  const tabItems = useMemo(
    () => [
      {
        key: 'unread',
        label: (
          <span>
            Unread{' '}
            <Badge
              className="ml-1"
              color={unreadNotificationsColor}
              count={unreadNotifications.length}
            />
          </span>
        ),
        children: (
          <div className="flex flex-col">
            {loading ? (
              <InboxNotificationLoader count={3} />
            ) : unreadNotifications.length === 0 ? (
              noDataMessage(false)
            ) : (
              unreadNotifications.map((notification) => (
                <InboxNotificationItem
                  key={notification.id}
                  item={notification}
                />
              ))
            )}
          </div>
        ),
      },
      {
        key: 'all',
        label: 'All',
        children: (
          <div className="flex flex-col">
            {isLoadingAllNotifications ? (
              <InboxNotificationLoader count={20} />
            ) : isErrorAllNotifications ? (
              <div className="pt-4 text-red-5">
                Failed to load notifications.
                <Button
                  className="ml-2"
                  type="default"
                  size="small"
                  onClick={() => refetchAllNotifications()}
                >
                  Retry
                </Button>
              </div>
            ) : !allNotificationsData ||
              allNotificationsData.notifications.length === 0 ? (
              noDataMessage(true)
            ) : (
              allNotificationsData?.notifications.map((notification) => (
                <InboxNotificationItem
                  key={notification.id}
                  item={notification}
                />
              ))
            )}
          </div>
        ),
      },
    ],
    [
      loading,
      unreadNotifications,
      isLoadingAllNotifications,
      isErrorAllNotifications,
      allNotificationsData,
      refetchAllNotifications,
    ]
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      styles={{ body: { paddingTop: 4 } }}
      title={
        <div className="flex items-center justify-between">
          <span>Inbox</span>
          <Button
            type="text"
            icon={<SettingOutlined style={{ fontSize: 16 }} />}
            disabled
          />
        </div>
      }
      footerActions={[
        {
          text: 'Mark all as read',
          onClick: () => markAsReadMutation.mutate(),
          loading: markAsReadMutation.isPending,
          disabled: markAsReadMutation.isPending,
        },
      ]}
    >
      <InboxNotificationProvider onClose={onClose}>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => onTabChange(key as NotificationTabType)}
          items={tabItems}
          className="h-full"
        />
      </InboxNotificationProvider>
    </Drawer>
  );
}
